import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from './firebase';
import { addDoc, collection } from "firebase/firestore";
import { Icons } from './lib/Icons';
import { useSchoolId } from './lib/auth/schoolContext';

const AddStudentForm = ({ open, setOpen, onStudentAdded }) => {
  const navigate = useNavigate();
  const { schoolId } = useSchoolId();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    yearGroup: "",
    phase: "primary",
    hasEHCP: false,
    academicLevel: "working at",
    specialNeeds: [],
    attendance: 95,
    interventionGroups: [],
    schoolId: schoolId,
  });

  const resetForm = () => {
    setForm({
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      yearGroup: "",
      phase: "primary",
      hasEHCP: false,
      academicLevel: "working at",
      specialNeeds: [],
      attendance: 95,
      interventionGroups: [],
      schoolId: schoolId,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSpecialNeedsChange = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      specialNeeds: checked 
        ? [...prev.specialNeeds, value]
        : prev.specialNeeds.filter(need => need !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting student:", form);
    console.log("🧾 Final student object:", JSON.stringify(form, null, 2));

    try {
      // Add timestamps
      const studentData = {
        ...form,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const docRef = await addDoc(collection(db, "students"), studentData);
      console.log("Student added with ID: ", docRef.id);
      alert("Student added successfully!");
      
      // Call the callback to refresh the students list
      if (onStudentAdded) {
        onStudentAdded();
      }
      
      // Reset form and close drawer
      resetForm();
      setOpen(false);
      
      // Navigate to the new student's profile
      navigate(`/student/${docRef.id}`);
    } catch (error) {
      console.error("Error adding student: ", error);
      alert("Failed to add student");
    }
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const yearGroups = {
    primary: ['Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'],
    secondary: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11']
  };

  const specialNeedsOptions = [
    'Autism Spectrum Disorder',
    'Attention Deficit Hyperactivity Disorder',
    'Dyslexia',
    'Dyscalculia',
    'Speech and Language Difficulties',
    'Social, Emotional and Mental Health',
    'Physical Disabilities',
    'Hearing Impairment',
    'Visual Impairment',
    'Moderate Learning Difficulties',
    'Severe Learning Difficulties',
    'Profound and Multiple Learning Difficulties'
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black transition-all duration-300 ease-in-out z-40 ${
          open ? 'bg-opacity-25 opacity-100' : 'bg-opacity-0 opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      />
      
      {/* Drawer */}
      <div className={`fixed inset-0 overflow-hidden z-50 ${open ? 'block' : 'hidden'}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <div 
              className={`pointer-events-auto w-screen max-w-md transform transition-all duration-300 ease-in-out ${
                open ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              <form onSubmit={handleSubmit} className="relative flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl dark:divide-white/10 dark:bg-gray-800 dark:after:absolute dark:after:inset-y-0 dark:after:left-0 dark:after:w-px dark:after:bg-white/10">
                <div className="h-0 flex-1 overflow-y-auto">
                  <div className="bg-indigo-700 px-4 py-6 sm:px-6 dark:bg-indigo-800">
                    <div className="flex items-center justify-between">
                      <h2 className="text-base font-semibold text-white">Add New Student</h2>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="relative rounded-md text-indigo-200 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white dark:text-indigo-300 dark:hover:text-white transition-colors duration-200"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close panel</span>
                          <Icons.XMark aria-hidden="true" className="size-6" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1">
                      <p className="text-sm text-indigo-300">
                        Fill in the information below to add a new student to your system.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-1 flex-col justify-between">
                    <div className="divide-y divide-gray-200 px-4 sm:px-6 dark:divide-white/10">
                      <div className="space-y-6 pt-6 pb-5">
                        {/* First Name */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <label htmlFor="firstName" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100">
                            First Name
                          </label>
                          <div className="mt-2">
                            <input
                              id="firstName"
                              name="firstName"
                              type="text"
                              placeholder="First Name"
                              value={form.firstName}
                              onChange={handleChange}
                              required
                              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:border-gray-600 transition-all duration-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            />
                          </div>
                        </div>

                        {/* Last Name */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <label htmlFor="lastName" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100">
                            Last Name
                          </label>
                          <div className="mt-2">
                            <input
                              id="lastName"
                              name="lastName"
                              type="text"
                              placeholder="Last Name"
                              value={form.lastName}
                              onChange={handleChange}
                              required
                              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:border-gray-600 transition-all duration-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            />
                          </div>
                        </div>

                        {/* Date of Birth */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <label htmlFor="dateOfBirth" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100">
                            Date of Birth
                          </label>
                          <div className="mt-2">
                            <input
                              id="dateOfBirth"
                              name="dateOfBirth"
                              type="date"
                              value={form.dateOfBirth}
                              onChange={handleChange}
                              required
                              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:focus:outline-indigo-500 dark:border-gray-600 transition-all duration-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            />
                          </div>
                        </div>

                        {/* Phase */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <label htmlFor="phase" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100">
                            Phase
                          </label>
                          <div className="mt-2">
                            <select
                              id="phase"
                              name="phase"
                              value={form.phase}
                              onChange={handleChange}
                              required
                              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:focus:outline-indigo-500 dark:border-gray-600 transition-all duration-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            >
                              <option value="primary">Primary</option>
                              <option value="secondary">Secondary</option>
                            </select>
                          </div>
                        </div>

                        {/* Year Group */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <label htmlFor="yearGroup" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100">
                            Year Group
                          </label>
                          <div className="mt-2">
                            <select
                              id="yearGroup"
                              name="yearGroup"
                              value={form.yearGroup}
                              onChange={handleChange}
                              required
                              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:focus:outline-indigo-500 dark:border-gray-600 transition-all duration-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            >
                              <option value="">Select Year Group</option>
                              {yearGroups[form.phase].map(yearGroup => (
                                <option key={yearGroup} value={yearGroup}>{yearGroup}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Academic Level */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <label htmlFor="academicLevel" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100">
                            Academic Level
                          </label>
                          <div className="mt-2">
                            <select
                              id="academicLevel"
                              name="academicLevel"
                              value={form.academicLevel}
                              onChange={handleChange}
                              required
                              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:focus:outline-indigo-500 dark:border-gray-600 transition-all duration-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            >
                              <option value="working below">Working Below</option>
                              <option value="working at">Working At</option>
                              <option value="working above">Working Above</option>
                            </select>
                          </div>
                        </div>

                        {/* Attendance */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <label htmlFor="attendance" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100">
                            Attendance (%)
                          </label>
                          <div className="mt-2">
                            <input
                              id="attendance"
                              name="attendance"
                              type="number"
                              min="0"
                              max="100"
                              placeholder="95"
                              value={form.attendance}
                              onChange={handleChange}
                              required
                              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:border-gray-600 transition-all duration-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            />
                          </div>
                        </div>

                        {/* EHCP Status */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <div className="relative flex items-start group">
                            <div className="absolute flex h-6 items-center">
                              <input
                                id="hasEHCP"
                                name="hasEHCP"
                                type="checkbox"
                                checked={form.hasEHCP}
                                onChange={handleChange}
                                className="relative size-4 appearance-none rounded border-2 border-gray-300 before:absolute before:inset-1 before:rounded before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-gray-600 dark:checked:border-indigo-500 dark:checked:bg-indigo-500 dark:focus-visible:outline-indigo-500 transition-all duration-200 hover:border-indigo-400 focus:border-indigo-500"
                              />
                            </div>
                            <div className="pl-7 text-sm/6">
                              <label htmlFor="hasEHCP" className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer transition-colors duration-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                                Has EHCP
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Special Needs */}
                        <div className="transform transition-all duration-200 ease-in-out hover:scale-[1.02]">
                          <h3 className="text-sm/6 font-medium text-gray-900 dark:text-gray-100 mb-3">Special Needs</h3>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {specialNeedsOptions.map((need) => (
                              <div key={need} className="relative flex items-start group">
                                <div className="absolute flex h-6 items-center">
                                  <input
                                    id={need}
                                    name="specialNeeds"
                                    type="checkbox"
                                    value={need}
                                    checked={form.specialNeeds.includes(need)}
                                    onChange={handleSpecialNeedsChange}
                                    className="relative size-4 appearance-none rounded border-2 border-gray-300 before:absolute before:inset-1 before:rounded before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-gray-600 dark:checked:border-indigo-500 dark:checked:bg-indigo-500 dark:focus-visible:outline-indigo-500 transition-all duration-200 hover:border-indigo-400 focus:border-indigo-500"
                                  />
                                </div>
                                <div className="pl-7 text-sm/6">
                                  <label htmlFor={need} className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer transition-colors duration-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                                    {need}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex shrink-0 justify-end px-4 py-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-gray-100 dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-white/20 transition-all duration-200 hover:scale-105 active:scale-95"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-lg"
                  >
                    Add Student
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddStudentForm;

